import Logo from "../../images/logo.js";

// Context
import { useJob } from "../../hooks/useJobs.js";
import { useChats } from "../../hooks/useChats.js";
import { useAuth } from "../../hooks/useAuth.js";
import { useFeatures } from "../../hooks/useFeatures.js";
import { useRecorder } from "../../hooks/useRecorder.js";
import { useTheme } from "../../hooks/useTheme.js";
import { useLocation, useNavigate } from "react-router";
import Loader from "../common/Loader.js";
import { useTranslation } from "react-i18next";
import api from "../../services/api.js";
import { shouldReturnToEncounterList } from "../../utils/encounterNav.js";

// Portal pages own their full viewport chrome (their own header band,
// branding, and no sidebar). The global app Header would float white
// pills on top of those headers — hide it entirely for these routes.
const PORTAL_ROUTE_PREFIXES = ["/clinical-profile", "/patient-intake", "/stack-builder", "/voice-encounter", "/orderDelivered"];

function Header({
  setShowLeftSidebar,
  setShowRightSidebar,
  showRightSidebar,
  showClose,
  backTo,
  title,
  onBeforeClose = () => true,
}) {
  const { myJobs, userActions } = useJob();
  const { chat, setChat, filters, setFilters, importingChat } = useChats();
  // `showLeftSidebar` comes from the auth context (the source of truth that
  // `setShowLeftSidebar` mutates) — Layout never passed it as a prop, so the
  // toggle below was reading `undefined` and `!!!undefined` always resolved to
  // `true`, meaning the header button could only ever OPEN the sidebar, never
  // close it.
  const { hasNotifications, auth, changeLocation, location, updateProfile, iconSidebar, userPlan, showLeftSidebar } = useAuth();
  const { canUseFeature, hasFeature } = useFeatures();
  const { recording } = useRecorder();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  // Renamed to avoid shadowing the user's `location` (clinic) destructured
  // from useAuth above — both react-router and useAuth expose `location`.
  const routeLocation = useLocation();
  const { t } = useTranslation();

  // Suppress the global header on patient-facing portal routes — those pages
  // render their own self-contained header band and the floating theme/menu
  // pills land awkwardly over their gradient. (Provider-side routes keep the
  // full header.)
  if (PORTAL_ROUTE_PREFIXES.some((prefix) => routeLocation.pathname.startsWith(prefix))) {
    return null;
  }

  const handleToggleTheme = async () => {
    toggleTheme();

    const newTheme = isDark ? "light" : "dark";

    try {
      await updateProfile({
        custom: {
          themePreference: newTheme,
        },
      });
    } catch (error) {
      console.error("Failed to save theme preference:", error);
    }
  };

  const upgradePlan = async () => {
    let data = await api.getStripePaymentLink(window.location.href, window.location.href);
    window.location.assign(data.data.url);
  };

  return (
    <div className={"header" + (title !== undefined ? " header-with-title" : "")}>
      {title !== undefined ? (
        <h4 className="header-btn-bar m-0">{title}</h4>
      ) : (
        <>
          <div className="header-btn-bar header-mobile-nav mobile">
            <button
              disabled={recording}
              className="mobile-nav-toggle white no-border"
              style={{ visibility: !iconSidebar && "hidden" }}
              onClick={() => {
                if (setShowRightSidebar) setShowRightSidebar(false);
                // Returning from an open encounter to the list must DESELECT the
                // encounter — otherwise ChatDetail stays mounted and its sticky
                // patient-name header bleeds over the list (extension / narrow
                // layout). Mirror the logo (setChat + navigate "/"), but only on
                // the encounters surface so Settings — which shares this button —
                // keeps its plain drawer toggle.
                if (shouldReturnToEncounterList(routeLocation.pathname, chat)) {
                  navigate("/");
                  setChat();
                  if (setShowLeftSidebar) setShowLeftSidebar(true);
                } else if (setShowLeftSidebar) {
                  setShowLeftSidebar(!!!showLeftSidebar);
                }
              }}
            >
              <span className="material-symbols-outlined">{iconSidebar || "stethoscope"}</span>
            </button>
          </div>
          <div
            className="logo-wrapper"
            onClick={() => {
              if (recording) return;
              // Route to the kanban/home for whichever module the user is currently in.
              const inPeptides = window.location.pathname.startsWith("/peptides");
              if (inPeptides) {
                // Peptides has a dedicated kanban view — SPA navigate keeps the snappy feel.
                navigate("/peptides?view=orders");
              } else {
                // DME / EHR / sales — adaptive "/" often renders the same component the user
                // is already viewing, so SPA navigate looks like nothing happened. Scroll to
                // top + a brief opacity pulse on .body-wrapper gives UX feedback that the
                // click registered, without a real page reload (which would bounce auth).
                window.scrollTo({ top: 0, behavior: "smooth" });
                const body = document.querySelector(".body-wrapper");
                if (body) {
                  body.classList.remove("refreshing");
                  void body.offsetWidth; // force reflow so animation restarts on rapid clicks
                  body.classList.add("refreshing");
                  setTimeout(() => body.classList.remove("refreshing"), 500);
                }
                navigate("/");
              }
              setFilters({ ...filters, insuranceGrid: false });
              setChat();
              if (setShowRightSidebar) setShowRightSidebar(false);
              if (setShowLeftSidebar) setShowLeftSidebar(true);
            }}
          >
            <Logo></Logo>
          </div>
        </>
      )}

      <div className="header-btn-bar header-actions">
        {importingChat && (
          <div className="vertical-align mr-10" style={{ whiteSpace: "nowrap" }}>
            <Loader className="mr-10"></Loader>
            <b>{t("header.importingEncounter")}</b>
          </div>
        )}

        {userPlan?.userSubscriptionStatus === "TRIALING" && hasFeature("encounters") && (
          <>
            <div className="badge gray">
              {t("header.notesLeft", { count: Math.max(0, userPlan.maxNotes - userPlan.totalNotes) })}
            </div>
            <button className="button small mr-20" onClick={upgradePlan}>
              {t("header.upgradeNow")}
            </button>
          </>
        )}

        {auth.user.locations && auth.user.locations.length > 0 && (
          <select
            className="desktop"
            value={location}
            onChange={(e) => {
              if (canUseFeature("locations")) {
                changeLocation(e.target.value);
              }
            }}
            disabled={auth?.user?.locations?.length === 1}
          >
            {auth.user.locations.map((loc, k) => (
              <option value={loc.id} key={"loc_" + k}>
                {loc.name}
              </option>
            ))}
            {auth?.user?.locations?.length > 1 && (
              <option value="" key={"loc_unassgned"}>
                {t("common.all")}
              </option>
            )}
          </select>
        )}

        <button
          className="theme-toggle white no-border"
          onClick={handleToggleTheme}
          aria-label={isDark ? t("header.switchToLight") : t("header.switchToDark")}
          title={isDark ? t("header.switchToLight") : t("header.switchToDark")}
        >
          <span className="material-icons-outlined">{isDark ? "light_mode" : "dark_mode"}</span>
        </button>

        {setShowRightSidebar && (
          <button
            disabled={recording}
            className="white no-border btn-round"
            onClick={() => {
              setShowLeftSidebar(false);
              setShowRightSidebar(!!!showRightSidebar);
            }}
          >
            {hasNotifications && <div className="badge-to-read menu"></div>}
            <i className="notranslate material-icons">menu</i>
            {Object.keys(myJobs).length + userActions.length > 0 && (
              <div className="task-count">{Object.keys(myJobs).length + userActions.length}</div>
            )}
          </button>
        )}

        {showClose && (
          <button
            className="white no-border btn-close"
            onClick={() => {
              setChat();
              onBeforeClose() && navigate(backTo || "/home");
            }}
          >
            <span className="notranslate material-icons">close</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default Header;
